import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { DaylightAction } from "../../../../modules/content/components/DaylightAction/DaylightAction";
import { DaylightMedia } from "../../../../modules/content/components/DaylightMedia/DaylightMedia";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import { DaylightSection } from "../../../../modules/content/components/DaylightSection/DaylightSection";
import type { ISplitHeroBlockProps, SplitHeroSide } from "./SplitHeroBlock.props";

/** "ENERGIE. TRANSFORMIERT." splits so the trailing sentence can carry the accent color. */
export const splitHeroHeading = (heading: string): [string, string | null] => {
  const boundary = heading.trim().match(/^(.+?[.!?])\s+(.+)$/su);
  return boundary?.[1] && boundary[2] ? [boundary[1], boundary[2]] : [heading.trim(), null];
};

const HeroHalf = ({
  priority,
  reverse,
  side,
}: {
  priority?: boolean;
  reverse?: boolean;
  side: SplitHeroSide;
}) => (
  <div className="relative min-w-0 flex-1 overflow-hidden transition-[flex-grow] duration-700 ease-out hover:grow-[1.15] motion-reduce:transition-none motion-reduce:hover:grow-[1]">
    <DaylightMedia
      className="absolute inset-0 h-full border-0"
      imageClassName="object-cover"
      media={side.image.image}
      priority={priority}
      sizes="(min-width: 1024px) 50vw, 50vw"
    />
    <div
      className={cn(
        "absolute inset-0",
        reverse
          ? "bg-linear-to-l from-terminal/40 via-transparent to-terminal/60"
          : "bg-linear-to-r from-terminal/40 via-transparent to-terminal/60",
      )}
    />
  </div>
);

const HeroCornerLink = ({ align, side }: { align: "left" | "right"; side: SplitHeroSide }) => {
  const Link = getComponent("Link");
  if (!side.link) return null;
  return (
    <Link
      aria-label={side.link.accessibilityLabel || undefined}
      className={cn(
        "daylight-label-micro text-northsea transition-colors duration-200 hover:text-graphite",
        align === "right" && "text-right",
      )}
      href={side.link.href}
      rel={side.link.openInNewTab ? "noopener noreferrer" : undefined}
      target={side.link.openInNewTab ? "_blank" : undefined}
    >
      {side.link.label}
    </Link>
  );
};

export const SplitHeroBlock = ({
  alignment,
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  left,
  parentClass,
  right,
  spacing,
}: ISplitHeroBlockProps) => {
  if (!left?.image && !right?.image) return null;

  const [firstLine, secondLine] = heading ? splitHeroHeading(heading) : [null, null];

  return (
    <DaylightSection
      className={cn("flex min-h-screen flex-col justify-center border-t-0", parentClass)}
      containerClassName="flex w-full flex-1 flex-col"
      id={anchorId}
      spacing={spacing}
    >
      <div className="relative isolate min-h-[clamp(30rem,72vh,52rem)] w-full flex-1 overflow-hidden border border-border">
        <div className="absolute inset-0 flex">
          {left?.image ? <HeroHalf priority side={left} /> : null}
          {right?.image ? <HeroHalf reverse side={right} /> : null}
        </div>
        <div className="absolute inset-0 z-[1] bg-terminal/40 md:hidden" />
        <div className="absolute inset-y-0 left-1/2 z-[2] w-px -translate-x-1/2 bg-primary/60" />
        <div className="absolute inset-0 z-10 flex flex-col justify-between gap-10 p-6 md:p-10">
          <div className="flex items-start justify-between gap-4">
            {left?.note ? (
              <span className="daylight-label-micro text-northsea">{left.note}</span>
            ) : (
              <span />
            )}
            {right?.note ? (
              <span className="daylight-label-micro text-right text-northsea">{right.note}</span>
            ) : null}
          </div>
          <div className="mx-auto flex w-full max-w-4xl flex-col items-center text-center">
            {eyebrow ? (
              <p className="daylight-label mb-4 tracking-[0.3em] text-primary md:mb-6">{eyebrow}</p>
            ) : null}
            {firstLine ? (
              <h1 className="text-balance font-daylight-display text-[clamp(2.5rem,9.6vw,12rem)] font-bold leading-[0.85] tracking-[-0.04em] text-graphite">
                {firstLine}
                {secondLine ? (
                  <>
                    <br />
                    <span className="text-primary">{secondLine}</span>
                  </>
                ) : null}
              </h1>
            ) : null}
            {description ? (
              <DaylightRichText
                className="mt-6 max-w-2xl text-lg text-northsea text-balance md:mt-8 md:text-xl"
                content={description}
              />
            ) : null}
            {buttons?.length ? (
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                {buttons.map((button, index) => (
                  <DaylightAction action={button} key={`${button.href}-${index}`} />
                ))}
              </div>
            ) : null}
          </div>
          <div className="flex items-end justify-between gap-4">
            {left?.link ? <HeroCornerLink align="left" side={left} /> : <span />}
            {right?.link ? <HeroCornerLink align="right" side={right} /> : null}
          </div>
        </div>
      </div>
    </DaylightSection>
  );
};

SplitHeroBlock.displayName = "Block::SplitHeroBlock";
